import $ from 'jquery'
import { Util } from 'bootstrap'

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const NAME = 'colorpicker'
const DATA_KEY = `opp.${NAME}`
const EVENT_KEY = '.' + DATA_KEY
const DATA_API_KEY = '.data-api'
const JQUERY_NO_CONFLICT = $.fn[NAME]
// Rename bootstrap-colopicker plugin so we can still use it.
$.fn['_' + NAME] = JQUERY_NO_CONFLICT

const Default = {
  color: '#000000',
  format: 'hex'
}

const DefaultType = {
  color: 'string',
  format: 'string'
}

const Selector = {
  COLOR_INPUT: '.color-input',
  COLOR_PICKER_UI: '.color-picker-ui',
  DATA_TOGGLE: '[data-toggle="color-picker"]',
  INPUT: 'input'
}

const Event = {
  CHANGE_DATA_API: `change${EVENT_KEY}${DATA_API_KEY} colorpickerChange${EVENT_KEY}${DATA_API_KEY}`
}

/**
 * ------------------------------------------------------------------------
 * Class Definition
 * ------------------------------------------------------------------------
 */

class ColorPicker {
  constructor(element, config) {
    this._element = element
    this._config = this._getConfig(config)
    this._color = this._config.color

    this._colorInputElem = this._getColorInput()
    this._colorPickerUiElem = this._getColorPickerUi()

    // Update sub-components
    this._update()
  }

  // Public

  color(newColor) {
    if (!newColor || newColor === this._color) {
      return this._color
    }

    // Update internal state
    this._color = newColor

    // Update sub-components
    this._update()
  }

  // Private

  _getConfig(config) {
    config = {
      ...Default,
      ...config
    }
    Util.typeCheckConfig(NAME, config, DefaultType)
    return config
  }

  _update() {
    this._updateColorInput()
    this._updateColorPickerUi()
  }

  _updateColorInput() {
    ColorPicker._updateColorInputValue(
      this._colorInputElem,
      this._color
    )
  }

  _updateColorPickerUi() {
    ColorPicker._updateColorPickerUiValue(
      this._colorPickerUiElem,
      this._color
    )
  }

  _getColorInput() {
    return this._element.querySelector(Selector.COLOR_INPUT)
  }

  _getColorPickerUi() {
    const elem = this._element.querySelector(Selector.COLOR_PICKER_UI)
    if (elem) {
      return $(elem)._colorpicker({
        autoInputFallback: false,
        container: true,
        inline: true,
        input: false,
        useAlpha: false
      })
    }
  }

  // Static

  static _updateColorPickerUiValue(elem, newValue) {
    const oldValue = $(elem)._colorpicker(elem, 'getValue')

    if (oldValue !== newValue) {
      // Update value and trigger colorpickerChange event.
      $(elem)._colorpicker('setValue', newValue)
    }

    return newValue
  }

  static _updateColorInputValue(elem, newValue) {
    const $input = $(elem).is(Selector.INPUT) ? $(elem) : $(elem).find('input').first()
    const oldValue = $input.val()

    if (oldValue !== newValue) {
      // Update value and trigger change event.
      $input.val(newValue).change()
    }

    return newValue
  }

  static _jQueryInterface(config, ...args) {
    return this.each(function () {
      let data = $(this).data(DATA_KEY)
      const _config = {
        ...Default,
        ...$(this).data(),
        ...typeof config === 'object' && config ? config : {}
      }

      if (!data) {
        data = new ColorPicker(this, _config)
        $(this).data(DATA_KEY, data)
      }

      if (typeof config === 'string') {
        if (typeof data[config] === 'undefined') {
          throw new TypeError(`No method named "${config}"`)
        }

        data[config](...args)
      }
    })
  }
}

/**
 * ------------------------------------------------------------------------
 * Data Api implementation
 * ------------------------------------------------------------------------
 */

// Init color picker
ColorPicker._jQueryInterface.call($(Selector.DATA_TOGGLE))

$(document).on(Event.CHANGE_DATA_API, Selector.DATA_TOGGLE, function (event) {
  let color

  if (event.color) {
    color = event.color.toString()
  } else if ($(event.target).is(':valid')) {
    color = $(event.target).val()
  }

  if (color) {
    ColorPicker._jQueryInterface.call($(this), 'color', color)
  }
})

/**
 * ------------------------------------------------------------------------
 * jQuery
 * ------------------------------------------------------------------------
 */

$.fn[NAME] = ColorPicker._jQueryInterface
$.fn[NAME].Constructor = ColorPicker
$.fn[NAME].noConflict = () => {
  $.fn[NAME] = JQUERY_NO_CONFLICT
  return ColorPicker._jQueryInterface
}

export default ColorPicker
